import { inject, Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { ChatCreate, ChatDelete, ChatGetAll } from './chat.actions';
import { LoadingEnd, LoadingStart } from '../../core/loading.state';
import { ChatService } from './chat.service';
import { finalize, from, of, switchMap, tap } from 'rxjs';
import { insertItem, patch, removeItem } from '@ngxs/store/operators';
import { Router } from '@angular/router';
import { ChatBase } from '@aimi/models';
import { ChatConversationReset } from './chat-conversation/chat-conversation.action';
import { ChatConversationState } from './chat-conversation/chat-conversation.state';

interface ChatStateModel {
  chats: ChatBase[];
}

@State<ChatStateModel>({
  name: 'ChatState',
  defaults: {
    chats: [],
  },
})
@Injectable()
export class ChatState {
  #chatService = inject(ChatService);
  #router = inject(Router);
  #store = inject(Store);

  @Selector() static chats(state: ChatStateModel) {
    return state.chats;
  }

  static chat(chatId: string) {
    return createSelector([this.chats], (chats) => {
      return chats.find((c) => c.id === chatId);
    });
  }

  @Action(ChatCreate) createChat(
    ctx: StateContext<ChatStateModel>,
    action: ChatCreate,
  ) {
    ctx.dispatch(new LoadingStart(this.constructor.name));
    return this.#chatService.createChat(action.payload).pipe(
      tap((chatResponse) => {
        const { messages: _messages, ...chat } = chatResponse;
        ctx.setState(
          patch({
            chats: insertItem(chat, 0),
          }),
        );
      }),
      switchMap((chatResponse) => {
        const { messages, ...chat } = chatResponse;
        return of(this.#router.navigate(['portal', 'chat', chat.id]));
      }),
      finalize(() => ctx.dispatch(new LoadingEnd(this.constructor.name))),
    );
  }

  @Action(ChatGetAll)
  getAll(ctx: StateContext<ChatStateModel>) {
    ctx.dispatch(new LoadingStart(this.constructor.name));
    return this.#chatService.getChats().pipe(
      tap((chats) => {
        ctx.patchState({
          chats,
        });
      }),
      finalize(() => ctx.dispatch(new LoadingEnd(this.constructor.name))),
    );
  }

  @Action(ChatDelete)
  deleteChat(ctx: StateContext<ChatStateModel>, action: ChatDelete) {
    ctx.dispatch(new LoadingStart(this.constructor.name));
    return this.#chatService.deleteChat(action.chatId).pipe(
      tap(() => {
        ctx.setState(
          patch({
            chats: removeItem<ChatBase>((c) => c.id === action.chatId),
          }),
        );
      }),
      switchMap(() => {
        const conversationChatId = this.#store.selectSnapshot(
          ChatConversationState.chatId,
        );
        if (conversationChatId !== action.chatId) {
          return of(null);
        }
        return from(this.#router.navigateByUrl('/portal/chat/new')).pipe(
          switchMap(() => this.#store.dispatch(new ChatConversationReset())),
        );
      }),
      finalize(() => ctx.dispatch(new LoadingEnd(this.constructor.name))),
    );
  }
}
